import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Avatar, Menu, MenuItem, Divider, Button,
} from '@mui/material';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import PersonIcon       from '@mui/icons-material/Person';
import LogoutIcon       from '@mui/icons-material/Logout';
import DashboardIcon    from '@mui/icons-material/Dashboard';
import StorefrontIcon   from '@mui/icons-material/Storefront';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import { useAuth }      from '../../context/AuthContext';

const SERVICE_CONFIG = {
  restaurants: { label: 'Restaurants',   color: '#1a1a1a', accent: '#552583' },
  vtc:         { label: 'VTC',           color: '#1a1a1a', accent: '#1d4ed8' },
  logistics:   { label: 'Logistique',    color: '#1a1a1a', accent: '#b45309' },
  courses:     { label: 'Courses',       color: '#1a1a1a', accent: '#15803d' },
  carpool:     { label: 'Covoiturage',   color: '#1a1a1a', accent: '#0e7490' },
  'car-rental':{ label: 'Location',      color: '#1a1a1a', accent: '#7c3aed' },
};

export default function NavbarService({ service }) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const cfg = SERVICE_CONFIG[service] || { label: service, color: '#1a1a1a', accent: '#552583' };

  const handleLogout = () => { logout(); setAnchorEl(null); navigate('/'); };

  return (
    <Box component="header" sx={{
      position: 'sticky', top: 0, zIndex: 100,
      bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
      borderBottom: '1px solid rgba(0,0,0,0.07)',
      px: { xs: 2, sm: 3, md: 4 }, py: 0,
      height: { xs: 52, md: 56 },
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      {/* Left : retour + logo + service */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
        <IconButton component={Link} to="/" size="small"
          sx={{ color: 'rgba(0,0,0,0.45)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '8px', width: 32, height: 32, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
          <ArrowBackIcon sx={{ fontSize: 15 }} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component={Link} to="/"
            sx={{ fontWeight: 900, color: '#552583', textDecoration: 'none', letterSpacing: '0.1em', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            USAFIRI
          </Typography>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.2)' }} />
          <Box sx={{
            px: 1, py: 0.25, borderRadius: '5px',
            bgcolor: `${cfg.accent}12`,
            border: `1px solid ${cfg.accent}30`,
          }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.accent, letterSpacing: '0.04em' }}>
              {cfg.label}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right : compte */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isAuthenticated ? (
          <>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ p: 0 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#552583', fontSize: '0.72rem', fontWeight: 700 }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { mt: 1, minWidth: 180, borderRadius: '10px', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: '#1a1a1a' }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>{user?.email}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { navigate('/dashboard'); setAnchorEl(null); }}
                sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.2 }}>
                <DashboardIcon sx={{ fontSize: 17, color: '#888' }} /> Mon tableau de bord
              </MenuItem>
              {(user?.user_type === 'restaurant_owner') && (
                <MenuItem onClick={() => { navigate('/partner/restaurant'); setAnchorEl(null); }}
                  sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.2 }}>
                  <StorefrontIcon sx={{ fontSize: 17, color: '#888' }} /> Mon restaurant
                </MenuItem>
              )}
              {(user?.user_type === 'delivery') && (
                <MenuItem onClick={() => { navigate('/partner/delivery'); setAnchorEl(null); }}
                  sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.2 }}>
                  <DeliveryDiningIcon sx={{ fontSize: 17, color: '#888' }} /> Mode livreur
                </MenuItem>
              )}
              <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}
                sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.2 }}>
                <PersonIcon sx={{ fontSize: 17, color: '#888' }} /> Mon profil
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ fontSize: '0.84rem', gap: 1.5, py: 1.2, color: '#b91c1c' }}>
                <LogoutIcon sx={{ fontSize: 17 }} /> Déconnexion
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button component={Link} to="/login" size="small"
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: '#1a1a1a', border: '1px solid rgba(0,0,0,0.15)', borderRadius: '8px', px: 1.5, py: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
              Connexion
            </Button>
            <Button component={Link} to="/register" size="small"
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', bgcolor: '#552583', color: 'white', borderRadius: '8px', px: 1.5, py: 0.5, '&:hover': { bgcolor: '#33154f' } }}>
              Inscription
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
